import { useEffect, useState } from "react";
import client from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

/**
 * Single-select, search-as-you-type dealer picker. Same `/dealers?q=` search
 * this app already uses for the (multi-select) activation dealer picker —
 * see DealerPickerDialog in pages/ActivationDetail.jsx.
 */
export function DealerSearchPicker({ open, onOpenChange, onPick }) {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!open) return;
    setQ("");
    setItems([]);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(async () => {
      const { data } = await client.get("/dealers", { params: q ? { q, limit: 50 } : { limit: 50 } });
      setItems(data.items || []);
    }, 200);
    return () => clearTimeout(timer);
  }, [q, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="dealer-search-picker">
        <DialogHeader>
          <DialogTitle>{t("templatePreview.selectDealerTitle")}</DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="dealer-search-picker-input"
            placeholder={t("templatePreview.searchPlaceholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
            autoFocus
          />
        </div>
        <div className="max-h-80 overflow-auto rounded-md border border-border">
          {items.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => onPick(d)}
              className="flex w-full cursor-pointer items-center gap-3 border-b border-border px-3 py-2 text-left hover:bg-muted"
              data-testid={`dealer-search-picker-row-${d.id}`}
            >
              <div className="flex-1">
                <div className="text-sm font-medium">{d.name}</div>
                <div className="font-mono text-[10px] text-muted-foreground">{d.global_id} · {d.company_id}</div>
              </div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
